/**
 * Should ultimately follow the design of the following Figma file:
 * https://www.figma.com/file/Ya7mCOIZJp1UdBCH3VxnEL/Components-v2-%5BWIP%5D?type=design&node-id=8-5527&mode=design&t=2gon1bvi20WHeyeL-11
 */

import { classNames } from "@/lib/utils";
import { Tab as HeadlessUiTab } from "@headlessui/react";
import { ComponentProps, forwardRef, Ref } from "react";

const List = ({ className, ...props }: ComponentProps<typeof HeadlessUiTab.List>, ref?: Ref<HTMLElement>) => (
  <HeadlessUiTab.List
    ref={ref}
    {...props}
    className={classNames("flex gap-20 border-b border-stroke-base-3 text-12-14", className)}
  />
);

const Panels = ({ className, ...props }: ComponentProps<typeof HeadlessUiTab.Panels>, ref?: Ref<HTMLElement>) => (
  <HeadlessUiTab.Panels ref={ref} {...props} className={classNames(className)} />
);

const Panel = ({ className, ...props }: ComponentProps<typeof HeadlessUiTab.Panel>, ref?: Ref<HTMLElement>) => (
  <HeadlessUiTab.Panel ref={ref} {...props} className={classNames("focus:outline-none", className)} />
);

const Tab = (
  {
    className,
    Icon,
    size = 48,
    ...props
  }: ComponentProps<typeof HeadlessUiTab> & {
    Icon?: (props: React.ComponentPropsWithoutRef<"svg">) => JSX.Element;
    size?: 32 | 40 | 48;
  },
  ref?: Ref<HTMLElement>,
) => (
  <HeadlessUiTab
    ref={ref}
    {...props}
    className={classNames(
      "-mb-1 flex items-center gap-8 border-b border-transparent px-2 font-bold text-text-base-3 outline-none ui-selected:border-stroke-base-5 ui-selected:text-text-base-1",
      size === 32 && "h-32",
      size === 40 && "h-40",
      size === 48 && "h-48",
      className,
    )}
  >
    {Icon && <Icon className={"-my-2 -ml-2 h-16 w-16 text-icon-base-1 ui-selected:text-icon-base-1"} />}
    {props.children as React.ReactNode}
  </HeadlessUiTab>
);

const Tabs = {
  Group: HeadlessUiTab.Group,
  List: forwardRef(List),
  Panels: forwardRef(Panels),
  Panel: forwardRef(Panel),
  Tab: forwardRef(Tab),
};

export default Tabs;
